import { computed, ref } from 'vue';
import type { SettingElementInterface } from '@/types/items';
import { useAppDataStore } from '@/stores/app-data';
import { useCharacterDataStore } from '@/stores/character-data';
import { usePredicateStore } from '@/stores/predicate-store';
import { handleError } from '@/appdata/globals';
import { useElementGrid, type FetchResult } from '@/composables/useElementGrid';
import { useElementPredicates, buildSecondaryChips, type ElementType } from '@/composables/useElementPredicates';

export interface UseGuidedElementPickerConfig {
  /**
   * The API alias of the collection the step browses: the endpoint asked. It is not always the
   * `type` that picks the filters ('edges' browse 'advantages', 'hindrances' 'disadvantages').
   */
  alias: string;
  hydrate?: (el: SettingElementInterface) => Promise<SettingElementInterface | null>;
  initialFilters?: Record<string, unknown>;
  autoFetch?: boolean;
  /**
   * The step's draft. Decides whether the World filter is offered (see showWorldFilter).
   * Required: left out, a world-scoped draft would read as worldless and "Clear filters"
   * would wipe its own world scope.
   */
  character: () => { world?: unknown } | null | undefined;
}

/**
 * Picker plumbing shared by the guided-setup element steps (Hindrances, Edges,
 * Skills, Gear). Composes the (unmodified) predicate + grid composables, owns the
 * ElementBrowser template ref + open() trigger, the single hydrate-on-select
 * handler that lazily fetches the full element to fill the detail panel / summary,
 * and the Source/World filter rules the steps share (showWorldFilter, filtersActive,
 * clearFilters) — pair them with GuidedScopeFilters.vue, which renders the controls.
 *
 * It also builds the step's request from the step's API `alias`, so a step says only what it
 * browses, and passes on the session page size the grid hands it, which the request carries on a
 * first page (spec 2026-10-16).
 *
 * No domain logic lives here — add/remove, recalculation, trackers, and #details
 * stay in each step.
 *
 * NOTE: world scoping for guided setup is supplied via `predicateStore.worlds`,
 * which CharacterSetupFlow.vue sets to `[character.world]` before a step mounts.
 * `useElementPredicates` emits the world.uuid predicate from it. This is a shared
 * global dependency.
 *
 * `predicateStore.sources` is the same kind of shared slot for the Source filter,
 * which every step offers for every draft: it is set in one step and deliberately
 * carried across the rest, so no step clears it on mount/unmount —
 * CharacterSetupFlow.vue owns the reset.
 */
export function useGuidedElementPicker<T extends ElementType>(
  type: T,
  config: UseGuidedElementPickerConfig,
) {
  const store = useAppDataStore();
  const predicateStore = usePredicateStore();
  const characterData = useCharacterDataStore();
  const { filters, predicates, resetFilters } = useElementPredicates(type, config.initialFilters as any);
  const grid = useElementGrid({
    predicates,
    filterRefs: filters as any,
    // The grid hands over the session page size as `limit`. initializeElementChoices takes it after
    // the spinner flag and adds it to a first page only, requesting a page link as given
    // (spec 2026-10-16).
    fetchFn: (url, preds, limit) => characterData.initializeElementChoices(
      config.alias,
      url,
      preds,
      true,
      limit,
    ) as Promise<FetchResult>,
    autoFetch: config.autoFetch,
  });

  const browserRef = ref<{ show: () => void; hide: () => void } | null>(null);
  const open = () => browserRef.value?.show();

  // Source (the element's own `source` property — where it was published) is orthogonal to
  // campaign setting and useful for every draft, so the Source filter is always offered. World
  // (which settings an element is linked to) only makes sense for a worldless draft (the world
  // step was skipped) — a draft already scoped to a world doesn't need a second, redundant world
  // picker. See WorldFilterControl.vue's header comment for the full source-vs-world distinction.
  const showWorldFilter = computed(() => !config.character()?.world);

  const filtersActive = computed(() =>
    predicateStore.sources.length > 0
    // A world-scoped draft's predicateStore.worlds holds the character's own scope, not a user
    // filter — only count it once the world control is actually offered.
    || (showWorldFilter.value && predicateStore.worlds.length > 0)
    // The step's own secondary filter (severity, category, item type, attribute): the same
    // mapping the step reads its chips from, so the two cannot disagree.
    || buildSecondaryChips(type, filters.value).length > 0
    || filters.value.stringPartial.length > 0,
  );

  const clearFilters = () => {
    predicateStore.sources = [];
    // A world-scoped draft's predicateStore.worlds is the character's own scope, not a user
    // filter (see filtersActive) — clearing it here would silently widen the list, so only touch
    // it when the world control is actually offered.
    if (showWorldFilter.value) predicateStore.worlds = [];
    resetFilters();
  };

  const onSelect = async (element: SettingElementInterface) => {
    grid.selectedElement.value = element;
    if (element.summary || (element as any).description) return;
    try {
      const full = (await (config.hydrate?.(element) ?? store.retrieveElement(element))) as SettingElementInterface | null;
      if (!full) return;
      if (grid.selectedElement.value?.uuid === element.uuid) grid.selectedElement.value = full;
      const idx = grid.dataItems.value.findIndex((e) => e.uuid === element.uuid);
      if (idx >= 0) grid.dataItems.value.splice(idx, 1, full);
    } catch (error) {
      handleError(error);
    }
  };

  return {
    filters,
    predicates,
    resetFilters,
    showWorldFilter,
    filtersActive,
    clearFilters,
    dataItems: grid.dataItems,
    selectedElement: grid.selectedElement,
    loading: grid.loading,
    metadata: grid.metadata,
    links: grid.links,
    onSearch: grid.onSearch,
    onNextPage: grid.onNextPage,
    onPreviousPage: grid.onPreviousPage,
    fetch: grid.fetch,
    refresh: grid.refresh,
    browserRef,
    open,
    onSelect,
  };
}
